'use client';

import {
  createAmountInputCodec,
  formatFiatUnits,
  fundingAmountValidation,
  fundingFeedbackPresentation,
} from '@morfi/onramp-sdk';
import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';
import { FundingStatusHeader } from '../history/funding-status-header';

type FeedbackCheckout = Pick<
  FundingCheckout,
  | 'error'
  | 'state'
  | 'providerFailure'
  | 'returningMethodUnavailable'
  | 'preview'
  | 'busy'
  | 'changeMethod'
  | 'connect'
  | 'retry'
>;

export function FundingCheckoutFeedback({ checkout }: { checkout: FeedbackCheckout }): ReactNode {
  const t = useTranslations('onramp');
  const locale = useLocale();
  const sourceBounds =
    checkout.state === 'amount' && !checkout.preview.providerFailure?.amountBoundary
      ? checkout.preview.providerFailure?.sourceBounds
      : undefined;
  const feedback = fundingFeedbackPresentation(checkout);
  const destinationBounds =
    checkout.state === 'amount' ? checkout.preview.providerFailure?.destinationBounds : undefined;
  const destinationMessage = destinationBounds
    ? [
        destinationBounds.minimumUnits !== undefined
          ? t('amount_minimum', {
              amount: `${createAmountInputCodec(locale, destinationBounds.decimals).format(BigInt(destinationBounds.minimumUnits))} ${destinationBounds.currency === 'USDC_SOL' ? 'USDC' : destinationBounds.currency}`,
            })
          : null,
        destinationBounds.maximumUnits !== undefined
          ? t('amount_maximum', {
              amount: `${createAmountInputCodec(locale, destinationBounds.decimals).format(BigInt(destinationBounds.maximumUnits))} ${destinationBounds.currency === 'USDC_SOL' ? 'USDC' : destinationBounds.currency}`,
            })
          : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null;
  const validation = fundingAmountValidation(checkout);
  const sourceMessage = sourceBounds
    ? [
        sourceBounds.minimumUnits !== undefined
          ? t('payment_source_minimum', {
              amount: formatFiatUnits(
                sourceBounds.minimumUnits,
                sourceBounds.decimals,
                sourceBounds.currency,
                locale,
              ),
            })
          : null,
        sourceBounds.maximumUnits !== undefined
          ? t('payment_source_maximum', {
              amount: formatFiatUnits(
                sourceBounds.maximumUnits,
                sourceBounds.decimals,
                sourceBounds.currency,
                locale,
              ),
            })
          : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null;
  const notice =
    destinationMessage ??
    sourceMessage ??
    (feedback && (!feedback.title || checkout.state === 'amount') && !validation
      ? t(feedback.message)
      : null);
  const announcedError = useRef<string | null>(null);
  useEffect(() => {
    if (notice && announcedError.current !== notice) {
      showToast({
        id: TOAST_IDS.ONRAMP_CHECKOUT_ERROR,
        message: notice,
        variant: 'error',
      });
    }
    announcedError.current = notice;
  }, [notice]);
  return <FundingCheckoutFeedbackView checkout={checkout} />;
}

export function FundingCheckoutFeedbackView({
  checkout,
}: {
  checkout: FeedbackCheckout;
}): ReactNode {
  const t = useTranslations('onramp');
  const feedback = fundingFeedbackPresentation(checkout);
  if (checkout.state === 'amount' || checkout.state === 'status') return null;
  if (!feedback || (!feedback.title && !feedback.action)) return null;
  return (
    <div className="flex flex-col gap-4.5">
      {feedback.title && feedback.artwork ? (
        <FundingStatusHeader
          artwork={feedback.artwork}
          title={t(feedback.title)}
          description={t(feedback.message)}
        />
      ) : null}
      {feedback.action ? (
        <AnimatedButton
          className="h-11 rounded-control"
          loading={checkout.busy}
          disabled={checkout.busy}
          onClick={checkout[feedback.action]}
        >
          {t(feedback.actionLabel)}
        </AnimatedButton>
      ) : null}
    </div>
  );
}
